import { MutableRefObject, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { renderToString } from 'react-dom/server';
import { Customer, State, StateFamily, AnalysesErrorMessages, UserJourneyState } from '@algoan/dashboard/model';
import {
  defaultTableRowRenderer,
  TableRowProps,
  TableCellRenderer,
  defaultTableCellRenderer,
  TableCellProps,
  CellMeasurerProps,
  CellMeasurer,
  CellMeasurerCache,
} from 'react-virtualized';
import { isAnonymousCustomer, getDashboardTabRoute, useIntlFormatDate, Tabs } from '@algoan/dashboard/utils';
import { messages } from '../application/application-messages';
import Link from 'next/link';
import { useIntl, RawIntlProvider } from 'react-intl';
import { useSelector } from '@algoan/dashboard/store/store';
import {
  projectIdSelector,
  filterSelector,
  userRoleSelector,
  accessTokenOrganizationIdSelector,
  filterColumnsSelector,
  teamConfigSelector,
  sharingConfigSelector,
} from '@algoan/dashboard/store/selectors';
import {
  mapLogosNames,
  getColorFromDashboardState,
  customerHasAnalysis,
  convertUserJourneyStateToStateFamily,
  convertStateFamilyToIndicatorColor,
} from './customer-table-utils';
import { CustomerStateIndicator } from './customer-state-indicator';
import { customerListSelector } from './customer-table-selectors';
import { HighlightedSearch } from '../../shared-components';
import { TeamCell } from '../application/team-cell';
import { LogoTooltipMessage } from './customer-logo-tooltip-message';
import { PartnerLogo } from '../../shared-components/icons/partner-logo';
import { customerStateMessages } from '../../messages/filter-messages';
import { LinkIcon } from '../../shared-components/icons';
import { getInvitationUrl } from '../identity/utils';
import { addAlpha, ThemeProvider } from '@algoan/deep-dive/theme';
import { dashboardTheme } from '@algoan/dashboard/theme';
import { analysisStateTooltipMessages, messages as DashboardMessage } from '../dashboard/dashboard-messages';
import { mapLastFailedAnalysisErrorCodesToAnalysisMessage } from '@algoan/dashboard/store/utils';

export interface RowDataProps {
  rowData: Customer;
}

export const TableRow: React.FC<TableRowProps> = (props) => {
  const customerList = useSelector(customerListSelector);
  const organizationId = useSelector(accessTokenOrganizationIdSelector) as string;
  const projectId = useSelector(projectIdSelector) as string;
  const intl = useIntl();
  const { isInitiator } = useSelector(userRoleSelector);

  if (isAnonymousCustomer(customerList[props.index])) {
    return (
      <div
        key={props.rowData.id}
        data-event="click"
        data-tooltip-id="anonymous-folder-tooltip"
        data-tooltip-content={intl.formatMessage(messages.anonymousCustomer)}
      >
        {defaultTableRowRenderer({ ...props, key: props.rowData.id })}
      </div>
    );
  }

  if (isInitiator) {
    return <>{defaultTableRowRenderer({ ...props, key: props.rowData.id })}</>;
  }

  return (
    <Link
      key={props.rowData.id}
      href={getDashboardTabRoute(organizationId, projectId, props.rowData.id, Tabs.DECISION)}
    >
      {defaultTableRowRenderer({ ...props, key: props.rowData.id })}
    </Link>
  );
};

export const StateCell: React.FC<RowDataProps> = ({ rowData: customer }) => {
  const intl = useIntl();

  const userJourneyState = customer.reports.userJourneyState;
  const userSegment = customer.userSegment;
  const stateFamily = convertUserJourneyStateToStateFamily(userJourneyState, userSegment);

  const errorMessage =
    stateFamily === StateFamily.IN_ERROR &&
    mapLastFailedAnalysisErrorCodesToAnalysisMessage(customer.reports?.lastFailedAnalysis?.type);

  // Convert JSX to html because to display an image in the tooltip with the `data-tooltip-content`, you must use html
  const tooltipContent = renderToString(
    <RawIntlProvider value={intl}>
      <ThemeProvider customTheme={dashboardTheme}>
        <S.TooltipContainer>
          {userJourneyState === UserJourneyState.AGGREGATION_FINISHED_ERROR ||
          userJourneyState === UserJourneyState.FINISHED_ERROR ? (
            <div>{intl.formatMessage(DashboardMessage['failedAggregationErrorMessage'])}</div>
          ) : (
            <div>
              <S.Text>
                <CustomerStateIndicator
                  state={convertStateFamilyToIndicatorColor(stateFamily)}
                  isAnonymousCustomer={isAnonymousCustomer(customer)}
                />
                <span>{intl.formatMessage(analysisStateTooltipMessages[stateFamily])}</span>
              </S.Text>
              {errorMessage && (
                <S.Text>
                  {intl.formatMessage(
                    DashboardMessage[
                      errorMessage === AnalysesErrorMessages.TECHNICAL_ERROR
                        ? 'TechnicalErrorWithShortenedMessage'
                        : errorMessage
                    ],
                  )}
                </S.Text>
              )}
            </div>
          )}
        </S.TooltipContainer>
      </ThemeProvider>
    </RawIntlProvider>,
  );

  return (
    <S.StateContainer
      key={customer.id}
      data-effect="solid"
      data-tooltip-id="state-tooltip"
      data-tooltip-html={tooltipContent}
    >
      <CustomerStateIndicator
        state={convertStateFamilyToIndicatorColor(stateFamily)}
        isAnonymousCustomer={isAnonymousCustomer(customer)}
      />
    </S.StateContainer>
  );
};

export const CreatedAtCell: React.FC<TableCellProps> = (props) => {
  const formatDate = useIntlFormatDate();
  const filterColumns = useSelector(filterColumnsSelector);
  const filterData = useSelector(filterSelector);

  const shouldHighlightColumn = (path: string): boolean => filterColumns.includes(path) && !!filterData.inputValue;

  const highlightedCellRenderer: TableCellRenderer = (props) => (
    <HighlightedSearch cellData={(props.cellData as string) ?? ''} wordToSearch={filterData.inputValue ?? ''} />
  );

  const customer: Customer = props.rowData;
  const formattedDate = formatDate(customer.dashboard.createdAt);

  const actualCellProps = { ...props, cellData: formattedDate };
  return (
    <S.DateCellContainer>
      {shouldHighlightColumn('dashboard.createdAt')
        ? highlightedCellRenderer(actualCellProps)
        : defaultTableCellRenderer(actualCellProps)}
    </S.DateCellContainer>
  );
};

export const GroupCell: React.FC<RowDataProps> = ({ rowData: customer }) => {
  const teamConfigInformation = useSelector(teamConfigSelector);

  const team = teamConfigInformation?.teams.find((team) => customer.dashboard.team === team.name);

  if (customer.dashboard.team) {
    return (
      <TeamCell
        isAnonymousCustomer={isAnonymousCustomer(customer)}
        teamName={customer.dashboard.team}
        teamColor={team?.color}
      />
    );
  }

  return null;
};

export interface PartnersCellProps {
  rowData: Customer;
  columnIndex: number;
  dataKey: string;
  parent?: CellMeasurerProps['parent'];
  rowIndex: number;
  cache: MutableRefObject<CellMeasurerCache>;
}

export const PartnersCell: React.FC<PartnersCellProps> = ({
  rowData: customer,
  columnIndex,
  dataKey,
  parent,
  rowIndex,
  cache,
}) => {
  const intl = useIntl();
  const recipients = useSelector(sharingConfigSelector)?.recipients;

  const result = isAnonymousCustomer(customer);

  const tooltipContent = useMemo(() => {
    if (customer.shares && customer.shares.length > 0) {
      // Convert JSX to html because to display an image in the tooltip with the `data-tooltip-content`, you must use html
      return renderToString(
        <RawIntlProvider value={intl}>
          <LogoTooltipMessage shares={customer.shares} recipients={recipients} />
        </RawIntlProvider>,
      );
    }
    return undefined;
  }, [customer.shares, intl, recipients]);

  return (
    <CellMeasurer
      cache={cache.current}
      columnIndex={columnIndex}
      key={dataKey}
      parent={parent as CellMeasurerProps['parent']}
      rowIndex={rowIndex}
    >
      <S.Logos data-effect="solid" data-tooltip-id="partner-tooltip" data-tooltip-html={tooltipContent}>
        {mapLogosNames(customer.shares, recipients).map((partner, index) => (
          <PartnerLogo logo={partner.name} key={partner.name + index} isDisabled={result} />
        ))}
      </S.Logos>
    </CellMeasurer>
  );
};

export const CurrentStateCell: React.FC<RowDataProps> = ({ rowData: customer }) => {
  const intl = useIntl();
  const filterData = useSelector(filterSelector);
  const theme = useTheme();

  const state = customer.dashboard.currentState;

  const isNewWithAnalysis = customer.dashboard.currentState === State.NEW && customerHasAnalysis(customer);

  return (
    <S.Status
      {...getColorFromDashboardState(state, isAnonymousCustomer(customer), theme)}
      bold={isNewWithAnalysis && !filterData.inputValue}
    >
      {state in customerStateMessages && intl.formatMessage(customerStateMessages[state])}
    </S.Status>
  );
};

export const SpacerCell: React.FC = () => {
  const handleClick = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  return <S.Spacer onClick={handleClick} />;
};

export const InvitationCell: React.FC<RowDataProps> = ({ rowData: customer }) => {
  const theme = useTheme();

  const isCustomerCreatedWithInvitation = !!customer?.dashboard?.invitationHistory?.length;

  const handleClick = (event: React.MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  if (isCustomerCreatedWithInvitation && !isAnonymousCustomer(customer)) {
    return (
      <S.Link href={getInvitationUrl(customer.id)}>
        <LinkIcon color={theme.secondary.softBlue2} />
      </S.Link>
    );
  }

  return (
    <S.InvitationContainer onClick={handleClick}>
      <LinkIcon color={theme.secondary.skyBlue} />
    </S.InvitationContainer>
  );
};

const S = {
  DateCellContainer: styled.div`
    font-weight: inherit;
    white-space: pre;
  `,
  Logos: styled.div`
    display: flex;
    flex-wrap: wrap;
    margin: 0px -10px;
    padding: 0px 4px;
    white-space: normal;
    width: 100%;
  `,
  Status: styled.div<{ bold: boolean }>`
    ${({ bold }) => (bold ? 'font-weight: bold;' : '')}
  `,
  Link: styled(Link)`
    padding: 9px 30px;

    &:hover {
      background-color: ${({ theme }) => addAlpha(theme.algoanBlue, 0.05)};
    }
  `,
  InvitationContainer: styled.div`
    padding: 9px 15px;
    cursor: default;
  `,
  Spacer: styled.div`
    height: 100%;
    width: 100%;
  `,
  StateContainer: styled.div`
    padding: 0 25px;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  TooltipContainer: styled.div`
    display: flex;
    flex-direction: column;
  `,
  Text: styled.div`
    display: flex;
    align-items: center;

    & > span {
      margin-left: 10px;
    }
  `,
};
